import { NounKeys, ProductKeys1P } from '../../../common/constants/types';

/**
 * @internal Implementation detail of `useRovoConfigStore`. Do **not** import
 * this from anywhere other than `src/services/hooks/useRovoConfigStore.tsx` —
 * it is intentionally not part of the package's public API.
 *
 * The set of first-party / noun product keys that are genuine chat connectors
 * and should be synthesised into the store as 1P entries.
 *
 * `accessibleProducts` (from combined-config) is broader than this: it also
 * includes combined-only **sub-product** keys (e.g. `jira-core`,
 * `jira-software`) and other accessible products that are not chat connectors
 * (e.g. `avp`, `devai`). The FG-off knowledge endpoint never surfaced those as
 * connectors, so emitting them here produces phantom rows with no friendly
 * name and an icon URL that does not resolve. We therefore only synthesise 1P
 * entries for keys we explicitly recognise.
 *
 * Derived from the package's canonical constants so it stays in sync as new
 * first-party products / nouns are added:
 * - every {@link ProductKeys1P} value (except the exclusions below), plus
 * - the {@link NounKeys} that chat surfaces (`goal`, `project`), plus
 * - `loom` (a first-party connector handled via the friendly-name fallback).
 *
 * TODO RAOC-23: these are genuine first-party products but the chat knowledge
 * backend has no corresponding `KnowledgeSourceType` for them yet, so they have
 * no chat `source` and their icon endpoint URLs do not resolve. They are
 * excluded here until the static-config icon lookup lands (see the icon TODO in
 * `getIconUrlForSource`). Re-include each one once it can render a resolvable
 * icon:
 * - `jira-product-discovery` (Jira Product Discovery)
 * - `cmdb` (Assets)
 * - `mercury` (Focus)
 * - `radar` (Talent)
 */
const FIRST_PARTY_KEYS_PENDING_CHAT_SOURCE: ReadonlySet<string> = new Set<string>([
	ProductKeys1P.JiraProductDiscovery,
	ProductKeys1P.Assets,
	ProductKeys1P.Focus,
	ProductKeys1P.Talent,
]);

/**
 * First-party product keys that are **permanently** excluded from the store —
 * they are not chat connectors and should never be synthesised, regardless of
 * any future chat-source / icon work.
 *
 * - `townsquare` (Atlas / Home) — Home is not a knowledge connector.
 */
const FIRST_PARTY_KEYS_EXCLUDED: ReadonlySet<string> = new Set<string>([ProductKeys1P.Atlas]);

const RECOGNISED_FIRST_PARTY_KEYS: ReadonlySet<string> = new Set<string>(
	[...Object.values(ProductKeys1P), NounKeys.goal, NounKeys.project, 'loom'].filter(
		(key) => !FIRST_PARTY_KEYS_PENDING_CHAT_SOURCE.has(key) && !FIRST_PARTY_KEYS_EXCLUDED.has(key),
	),
);

/**
 * @internal Only for use by `useRovoConfigStore`.
 *
 * Returns `true` when the given combined-config product key is a recognised
 * first-party / noun chat connector that should be synthesised into the store.
 */
export const isRecognisedFirstPartyConnector = (productKey: string): boolean =>
	RECOGNISED_FIRST_PARTY_KEYS.has(productKey);
